const baseUrl =
	(import.meta.env.VITE_API_BASE_URL as string | undefined) ||
	'https://uny1wgiali.execute-api.ap-southeast-2.amazonaws.com/dev';

async function http<T>(path: string, options: RequestInit): Promise<T> {
	const res = await fetch(`${baseUrl}${path}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			...(options.headers || {})
		}
	});
	if (!res.ok) {
		const contentType = res.headers.get('content-type') || '';
		try {
			if (contentType.includes('application/json')) {
				const data = (await res.json()) as any;
				const msg =
					(typeof data?.message === 'string' && data.message) ||
					(typeof data?.error === 'string' && data.error) ||
					JSON.stringify(data);
				throw new Error(msg || `Request failed: ${res.status}`);
			} else {
				const text = await res.text();
				throw new Error(text || `Request failed: ${res.status}`);
			}
		} catch (e: any) {
			throw new Error(e?.message || `Request failed: ${res.status}`);
		}
	}
	return (await res.json()) as T;
}

export type CreditBalance = {
	userId: string;
	initialCredits: number; // Free credits (15 initial)
	credits: number; // Paid credits from subscriptions
	createdAt: string;
	updatedAt: string;
};

export type CreditTransaction = {
	userId: string;
	transactionId: string;
	type: 'deduct' | 'add' | 'reset' | 'bonus';
	amount: number;
	balanceAfter: number;
	description?: string;
	createdAt: string;
};

export type CreditHistoryResponse = {
	items: CreditTransaction[];
	count: number;
	nextStartKey?: string;
};

export const CreditsService = {
	getBalance: (userId: string, authHeader: Record<string, string>) =>
		http<CreditBalance>(`/credits/${userId}`, {
			method: 'GET',
			headers: authHeader
		}),

	deduct: (
		userId: string,
		amount: number,
		description: string,
		authHeader: Record<string, string>
	) =>
		http<{
			success: boolean;
			userId: string;
			initialCredits: number;
			credits: number;
			createdAt: string;
			updatedAt: string;
		}>(`/credits/${userId}/deduct`, {
			method: 'POST',
			body: JSON.stringify({ amount, description }),
			headers: authHeader
		}),

	add: (
		userId: string,
		amount: number,
		type: 'add' | 'subscription' | 'bonus' | 'reset',
		description: string,
		authHeader: Record<string, string>
	) =>
		http<{
			success: boolean;
			userId: string;
			initialCredits: number;
			credits: number;
			createdAt: string;
			updatedAt: string;
		}>(`/credits/${userId}/add`, {
			method: 'POST',
			body: JSON.stringify({ amount, type, description }),
			headers: authHeader
		}),

	getHistory: (userId: string, limit?: number, startKey?: string, authHeader?: Record<string, string>) =>
		http<CreditHistoryResponse>(`/credits/${userId}/history${limit ? `?limit=${limit}${startKey ? `&startKey=${startKey}` : ''}` : ''}`, {
			method: 'GET',
			headers: authHeader || {}
		})
};
